import type { ABTest, ABTests } from '@lmn/lmn-commonlogic/dist/config/abtest/ABTestsTypes';
import type { SearchParams } from '@/server/searchParams.ts';
import { VARIANT_P, VARIANT_T } from '@lmn/lmn-commonlogic/dist/config/abtest/utils';
import { FORCE_CONFIG_URL_PARAM_PREFIX } from '@lmn/lmn-commonlogic/dist/config/configuration';
import { whenMultipleValuesTakeTheFirst } from '@lmn/lmn-commonlogic/dist/utils';
import { EXPERIMENT_NAMES } from '@/app/common/ABTesting/ABTestingVariants.ts';

type ABTestExtraInfo = ABTest & {
  forcedVariant: string;
};

type GetActiveABTestsProps = {
  abTests?: ABTests;
  searchParams: SearchParams;
};

// Resolve the variant forced via URL param, accepting both true/false and P/T aliases.
const resolveForcedVariant = (urlParamValue?: string): string => {
  if (urlParamValue === 'false' || urlParamValue === VARIANT_P) return VARIANT_P;
  if (urlParamValue === 'true' || urlParamValue === VARIANT_T) return VARIANT_T;
  return urlParamValue || '';
};

// Both spellings force, and the bare one wins: `?cux_x=&force_config_cux_x=T` does not force, because
// an empty bare value is not nullish.
// A repeated param (`?exp=T&exp=P`) arrives as an array despite the SearchParams type, so each
// spelling is normalised the way getABTestVariant normalises it - otherwise a doubled param reads as
// forcing here while commonlogic takes its first value and disagrees.
const getForcedByUrlValue = (experimentName: string, searchParams: SearchParams): string | undefined =>
  whenMultipleValuesTakeTheFirst(searchParams[experimentName]) ??
  whenMultipleValuesTakeTheFirst(searchParams[`${FORCE_CONFIG_URL_PARAM_PREFIX}${experimentName}`]);

const EXPERIMENT_NAME_SET: ReadonlySet<string> = new Set<string>(Object.values(EXPERIMENT_NAMES));

// Every URL parameter that forces something SAAB would otherwise have a say in: an experiment
// variant - `?cux_x=T` - or a config value, `?force_config_anything=...`, which can move the
// perimeter under the experiments. Presence is enough for the force_config_ prefix; a bare
// experiment name needs a value, because a valueless one is what getABTestVariant does not treat as
// forcing. `force_active_` is deliberately not here: getABTestsV2 already skips the SAAB call for
// those, and only for those.
export const getForcingUrlParamNames = (searchParams: SearchParams): string[] =>
  Object.entries(searchParams)
    .filter(
      ([key, value]) => key.startsWith(FORCE_CONFIG_URL_PARAM_PREFIX) || (!!value && EXPERIMENT_NAME_SET.has(key))
    )
    .map(([key]) => key);

// Return AB Tests that have a variant or are overridden, or were forced to a variant via URL param,
// annotated with any URL-forced variant.
export const getActiveABTests = ({ abTests, searchParams }: GetActiveABTestsProps): ABTestExtraInfo[] =>
  (abTests ?? [])
    .filter(
      (abTest) =>
        abTest.version || abTest.override || getForcedByUrlValue(abTest.experimentName, searchParams) !== undefined
    )
    .map((abTest) => ({
      ...abTest,
      forcedVariant: resolveForcedVariant(getForcedByUrlValue(abTest.experimentName, searchParams)),
    }));
